import {
  AppCard,
  Button,
  Icon,
  PageContainer,
  RouteTabLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiSwapHorizontal } from "@mdi/js";
import { Alert, Box, Tabs } from "@mui/material";
import { createFileRoute, Outlet, useMatchRoute } from "@tanstack/react-router";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  showQuotation,
  useListQuotationFiles,
  useShowQuotation,
} from "../../../../../__generated__/orval/endpoints/quotations/quotations";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";

const RouteComponent: FC = () => {
  const { quotationId } = Route.useParams();
  const loaderQuotation = Route.useLoaderData();
  const { data: currentQuotation } = useShowQuotation(Number(quotationId));
  const quotation = currentQuotation ?? loaderQuotation;
  const { t } = useTranslation();
  const matchRoute = useMatchRoute();
  const { data: quotationFiles } = useListQuotationFiles(Number(quotationId));
  const purchaseOrderPdfDocumentPath =
    quotation.purchase_order_item?.purchase_order?.pdf_document_path;
  const attachmentCount =
    (quotationFiles?.files.length ?? 0) +
    Number(Boolean(purchaseOrderPdfDocumentPath));
  const isOffersRoute = !!matchRoute({
    to: "/vbuyer/quotations/$quotationId/offers",
  });
  const isActivityRoute = !!matchRoute({
    to: "/vbuyer/quotations/$quotationId/activity",
  });
  const isAttachmentsRoute = !!matchRoute({
    to: "/vbuyer/quotations/$quotationId/attachments",
  });
  const isChatRoute =
    !!matchRoute({ to: "/vbuyer/quotations/$quotationId/chat" }) ||
    !!matchRoute({ to: "/vbuyer/quotations/$quotationId/doubts" });
  let selectedTab = "details";
  if (isOffersRoute) {
    selectedTab = "offers";
  } else if (isActivityRoute) {
    selectedTab = "activity";
  } else if (isAttachmentsRoute) {
    selectedTab = "attachments";
  } else if (isChatRoute) {
    selectedTab = "chat";
  }

  return (
    <PageContainer
      backTo="/vbuyer/quotations"
      maxWidth={isOffersRoute || isActivityRoute ? false : undefined}
      showBackButton
      subtitle={quotation.display_type}
      title={t("quotation.detail.quotationNumberTitle", {
        referenceNumber: quotation.reference_number,
      })}
    >
      <AppCard>
        {selectedTab === "details" && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Button
              onClick={() =>
                window.location.assign(
                  `/vbuyer/quotations/${quotationId}?legacy_interface=true`,
                )
              }
              startIcon={<Icon icon={mdiSwapHorizontal} />}
              variant="outlined"
            >
              {t("quotation.switchToLegacyButton")}
            </Button>
          </Box>
        )}
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={selectedTab}>
            <RouteTabLink
              label={t("common.details")}
              params={{ quotationId }}
              to="/vbuyer/quotations/$quotationId/details"
              value="details"
            />
            <RouteTabLink
              label={t("common.offers")}
              params={{ quotationId }}
              to="/vbuyer/quotations/$quotationId/offers"
              value="offers"
            />
            <RouteTabLink
              label={
                <Box
                  sx={{ alignItems: "center", display: "inline-flex", gap: 1 }}
                >
                  {t("common.attachments")}
                  {attachmentCount > 0 && (
                    <Box
                      component="span"
                      sx={{
                        alignItems: "center",
                        bgcolor: "primary.main",
                        borderRadius: "50%",
                        color: "primary.contrastText",
                        display: "inline-flex",
                        fontSize: "0.75rem",
                        height: 20,
                        justifyContent: "center",
                        minWidth: 20,
                        px: 0.5,
                      }}
                    >
                      {attachmentCount}
                    </Box>
                  )}
                </Box>
              }
              params={{ quotationId }}
              to="/vbuyer/quotations/$quotationId/attachments"
              value="attachments"
            />
            <RouteTabLink
              label={t("common.activity")}
              params={{ quotationId }}
              to="/vbuyer/quotations/$quotationId/activity"
              value="activity"
            />
            <RouteTabLink
              label={t("chat")}
              params={{ quotationId }}
              to="/vbuyer/quotations/$quotationId/chat"
              value="chat"
            />
          </Tabs>
        </Box>

        {!!quotation.is_threat && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t("quotation.detail.threatAlertMessage")}
          </Alert>
        )}

        <Box sx={{ pt: 3, width: "100%" }}>
          <Outlet />
        </Box>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/quotations/$quotationId",
)({
  beforeLoad({ context }) {
    const roles = context.auth.user?.roles_names ?? [];
    if (
      roles.includes("View Quotations") ||
      roles.includes("Edit Quotations")
    ) {
      return;
    }

    throw new ForbiddenError();
  },
  component: RouteComponent,
  loader: ({ params: { quotationId } }) =>
    showQuotation(parseInt(quotationId, 10)),
});
